/**
 * Real market data for India GCC analysis.
 *
 * All figures are publicly-sourced estimates compiled from cited reports.
 * Each datum carries its source so AI engines and prospects can verify.
 * Update DATA_AS_OF when refreshing.
 *
 * Sources used:
 *  - NASSCOM-Zinnov: "GCC India Landscape" 2024 annual report (public summary).
 *  - Mercer Total Remuneration Survey India 2024 (public press release tier).
 *  - AON Salary Increase Survey India 2024 (public press tier).
 *  - Naukri JobSpeak Index (free monthly).
 *  - Everest Group GCC Cost Index (public press tier).
 *  - LinkedIn Talent Insights (public posts and labor-market briefings).
 *  - State IT/ITeS vision documents and Invest India city briefs.
 *
 * Where multiple sources disagree we cite the conservative midpoint and label
 * tier-2 emerging-hub figures as "Estimates based on regional indices".
 */

export const DATA_AS_OF = '2024-Q4 / 2025-Q1';

// Industry-level totals
export const MARKET_OVERVIEW = {
  totalGccs: {
    value: '~1,700',
    label: 'Active India GCCs',
    source: 'NASSCOM-Zinnov GCC India Landscape Report 2024',
    sourceUrl: 'https://nasscom.in/'
  },
  totalEmployment: {
    value: '~1.9M',
    label: 'GCC professionals in India',
    source: 'NASSCOM 2024',
    sourceUrl: 'https://nasscom.in/'
  },
  marketSize: {
    value: '$64.6B',
    label: 'GCC industry revenue (FY24)',
    source: 'NASSCOM-Zinnov 2024',
    sourceUrl: 'https://nasscom.in/'
  },
  yoyHeadcountGrowth: {
    value: '~13%',
    label: 'YoY headcount growth',
    source: 'NASSCOM 2024',
    sourceUrl: 'https://nasscom.in/'
  },
  yoyRevenueGrowth: {
    value: '~11%',
    label: 'YoY revenue growth',
    source: 'NASSCOM 2024',
    sourceUrl: 'https://nasscom.in/'
  },
  costSavingsVsUS: {
    value: '50–60%',
    label: 'Typical cost savings vs US operations',
    source: 'Zinnov GCC Cost Index 2024 / Everest Group',
    sourceUrl: 'https://zinnov.com/'
  },
  averageAttrition: {
    value: '~20%',
    label: 'India IT/ITeS attrition',
    source: 'Aon India Salary Increase Survey 2024',
    sourceUrl: 'https://www.aon.com/'
  }
};

// 12 cities — tier-1 hubs + emerging tier-2
// salaryRange is for senior software engineer / equivalent (5–8 yrs experience)
// costIndex is relative to Bangalore = 100 baseline
// Numbers labeled "Estimate" are regional indices, not direct survey extracts.
export const CITIES = [
  // Tier-1 established GCC hubs
  {
    slug: 'bangalore',
    name: 'Bangalore (Bengaluru)',
    state: 'Karnataka',
    tier: 1,
    talentPool: '850K+',
    talentPoolNote: 'Total tech professionals; LinkedIn Talent Insights 2024',
    seniorEngineerSalary: '₹14–22L',
    salaryNote: 'Mercer / AON 2024 medians for 5–8 yr experience',
    infrastructureScore: 9.2,
    costIndex: 100,
    primaryVerticals: ['Software', 'Fintech', 'AI/ML', 'Product engineering'],
    notes: 'India\'s GCC capital; deepest engineering talent density.'
  },
  {
    slug: 'hyderabad',
    name: 'Hyderabad',
    state: 'Telangana',
    tier: 1,
    talentPool: '480K+',
    talentPoolNote: 'LinkedIn Talent Insights 2024',
    seniorEngineerSalary: '₹13–20L',
    salaryNote: 'Mercer / AON 2024',
    infrastructureScore: 9.0,
    costIndex: 92,
    primaryVerticals: ['Cloud / SaaS', 'Pharma R&D', 'BFSI', 'Cyber'],
    notes: 'Strong state IT incentive program; Microsoft, Apple, Google anchor footprints.'
  },
  {
    slug: 'chennai',
    name: 'Chennai',
    state: 'Tamil Nadu',
    tier: 1,
    talentPool: '420K+',
    talentPoolNote: 'LinkedIn Talent Insights 2024',
    seniorEngineerSalary: '₹12–18L',
    salaryNote: 'Mercer / AON 2024',
    infrastructureScore: 8.8,
    costIndex: 88,
    primaryVerticals: ['Auto / mobility', 'BFSI', 'Healthcare IT', 'Embedded'],
    notes: 'TIDEL parks + SEZ availability; lower attrition vs Bangalore.'
  },
  {
    slug: 'pune',
    name: 'Pune',
    state: 'Maharashtra',
    tier: 1,
    talentPool: '380K+',
    talentPoolNote: 'LinkedIn Talent Insights 2024',
    seniorEngineerSalary: '₹13–19L',
    salaryNote: 'Mercer / AON 2024',
    infrastructureScore: 8.7,
    costIndex: 90,
    primaryVerticals: ['Auto', 'Engineering R&D', 'BFSI ops', 'SaaS'],
    notes: 'Hinjewadi / Magarpatta / Kharadi clusters; strong R&D lineage.'
  },
  {
    slug: 'mumbai',
    name: 'Mumbai',
    state: 'Maharashtra',
    tier: 1,
    talentPool: '410K+',
    talentPoolNote: 'LinkedIn Talent Insights 2024',
    seniorEngineerSalary: '₹16–24L',
    salaryNote: 'Mercer / AON 2024 (financial-services premium)',
    infrastructureScore: 8.5,
    costIndex: 110,
    primaryVerticals: ['Investment banking', 'Insurance', 'Asset mgmt', 'Media'],
    notes: 'BKC / Powai clusters; the financial-services GCC default.'
  },
  {
    slug: 'delhi-ncr',
    name: 'Delhi NCR',
    state: 'Delhi / Haryana / UP',
    tier: 1,
    talentPool: '520K+',
    talentPoolNote: 'LinkedIn Talent Insights 2024',
    seniorEngineerSalary: '₹14–22L',
    salaryNote: 'Mercer / AON 2024',
    infrastructureScore: 8.6,
    costIndex: 105,
    primaryVerticals: ['Consulting', 'Telecom', 'Insurance', 'Government tech'],
    notes: 'Gurugram / Noida / Cyber City; strong consulting and telecom GCCs.'
  },

  // Emerging tier-2 hubs (estimates based on regional indices)
  {
    slug: 'coimbatore',
    name: 'Coimbatore',
    state: 'Tamil Nadu',
    tier: 2,
    talentPool: '~110K',
    talentPoolNote: 'Estimate; Tamil Nadu IT Policy 2021 / Invest India brief',
    seniorEngineerSalary: '₹8–14L',
    salaryNote: 'Estimate based on regional indices',
    infrastructureScore: 7.5,
    costIndex: 70,
    primaryVerticals: ['Engineering services', 'Textile-tech', 'IT services'],
    notes: 'TIDEL Park Coimbatore; cost-efficient delivery hub.'
  },
  {
    slug: 'hosur',
    name: 'Hosur',
    state: 'Tamil Nadu',
    tier: 2,
    talentPool: '~35K',
    talentPoolNote: 'Estimate; industrial cluster (auto/electronics)',
    seniorEngineerSalary: '₹7–12L',
    salaryNote: 'Estimate based on regional indices',
    infrastructureScore: 7.0,
    costIndex: 65,
    primaryVerticals: ['Auto / EV', 'Electronics manufacturing', 'Engineering'],
    notes: 'Tata Electronics, Ola, Ather anchor cluster; emerging engineering GCC option for hardware-adjacent roles.'
  },
  {
    slug: 'indore',
    name: 'Indore',
    state: 'Madhya Pradesh',
    tier: 2,
    talentPool: '~95K',
    talentPoolNote: 'Estimate; MP IT Policy 2023 / Invest India brief',
    seniorEngineerSalary: '₹8–13L',
    salaryNote: 'Estimate based on regional indices',
    infrastructureScore: 7.2,
    costIndex: 65,
    primaryVerticals: ['IT services', 'BPM', 'Fintech ops'],
    notes: 'IT Park 1/2/3 + Crystal IT Park; central-India low-cost option.'
  },
  {
    slug: 'visakhapatnam',
    name: 'Visakhapatnam (Vizag)',
    state: 'Andhra Pradesh',
    tier: 2,
    talentPool: '~60K',
    talentPoolNote: 'Estimate; AP IT Policy 4.0 / Invest India brief',
    seniorEngineerSalary: '₹7–12L',
    salaryNote: 'Estimate based on regional indices',
    infrastructureScore: 7.0,
    costIndex: 60,
    primaryVerticals: ['IT services', 'BPM', 'Logistics tech', 'GIS'],
    notes: 'AP Government Fintech Valley + IT SEZs; aggressive state incentives.'
  },
  {
    slug: 'goa',
    name: 'Goa',
    state: 'Goa',
    tier: 2,
    talentPool: '~35K',
    talentPoolNote: 'Estimate; Goa IT Policy 2024',
    seniorEngineerSalary: '₹9–15L',
    salaryNote: 'Estimate; lifestyle-driven premium',
    infrastructureScore: 7.5,
    costIndex: 75,
    primaryVerticals: ['Product engineering', 'Design / UX', 'Remote-first GCCs'],
    notes: 'Emerging digital-nomad-friendly hub; smaller talent pool but premium-quality.'
  },
  {
    slug: 'lucknow',
    name: 'Lucknow',
    state: 'Uttar Pradesh',
    tier: 2,
    talentPool: '~75K',
    talentPoolNote: 'Estimate; UP IT/ITeS Policy 2022 / Invest India brief',
    seniorEngineerSalary: '₹7–12L',
    salaryNote: 'Estimate based on regional indices',
    infrastructureScore: 6.8,
    costIndex: 60,
    primaryVerticals: ['BPM', 'IT services', 'Government tech'],
    notes: 'IT City Lucknow + HCL Technopark; UP government incentives for new GCCs.'
  }
];

// Source registry — used by the methodology callout on tools that consume this data
export const MARKET_DATA_SOURCES = [
  {
    name: 'NASSCOM-Zinnov GCC Landscape Report 2024',
    url: 'https://nasscom.in/',
    use: 'India GCC count, employment totals, market size, growth rates'
  },
  {
    name: 'Mercer Total Remuneration Survey India 2024',
    url: 'https://www.mercer.com/',
    use: 'Senior engineer salary medians by city'
  },
  {
    name: 'Aon India Salary Increase Survey 2024',
    url: 'https://www.aon.com/',
    use: 'Attrition benchmarks; salary inflation'
  },
  {
    name: 'Naukri JobSpeak Index',
    url: 'https://www.naukri.com/jobspeak',
    use: 'Monthly hiring activity index by city / function'
  },
  {
    name: 'Everest Group GCC Cost Index',
    url: 'https://www.everestgrp.com/',
    use: 'Cost-savings ranges vs US operations'
  },
  {
    name: 'LinkedIn Talent Insights (public)',
    url: 'https://business.linkedin.com/talent-solutions',
    use: 'City-level tech talent pool sizes'
  },
  {
    name: 'State IT/ITeS vision documents (Invest India city briefs)',
    url: 'https://www.investindia.gov.in/',
    use: 'Tier-2 city talent and incentive estimates'
  }
];

export default { DATA_AS_OF, MARKET_OVERVIEW, CITIES, MARKET_DATA_SOURCES };
